import { ForemanJobDetail } from '@ups/xplat/api/dto';
import { hasForemanLaborDraftEdits } from './foreman-labor-draft.utils';

/**
 * Eviction pass for the offline job store (#33948).
 *
 * The cached-priority merge only ever merges-into or pushes: it never walks the
 * cached list to drop what the server stopped returning. The single removal path
 * is `forceClearLocalData`, and that flag is read off REMOTE jobs, so a job that
 * has disappeared from the remote list can never carry it and can never be
 * cleared. The store was therefore append-only at two levels — whole jobs, and
 * the `employees` array inside a job that is still returned.
 *
 * Verified on STG 2026-09-18: changing the signed-in user's HRRef changed the
 * server-side list (web dropped to 0 hours) while an authoritative refetch on the
 * device went 39 -> 40 jobs with ZERO removals, and a job still in the list kept
 * an orphaned employee row whose ForemanEmployee / ForemanEmployeeJob / labors
 * returned 0 rows from the database.
 *
 * The whole risk in this pass is deleting work the server has never seen, so
 * every drop is gated on {@link hasUnsyncedLocalWork} and anything without a
 * server identity is kept.
 */

function trimId(value: unknown): string {
  return value != null ? String(value).trim() : '';
}

interface EmployeeRow {
  foremanEmployeeJobID?: string | null;
  labors?: Array<Parameters<typeof hasForemanLaborDraftEdits>[0]> | null;
}

/**
 * True when this dispatch row holds a labor edit the submit API has not stored.
 * Delegates to the draft helper so "unsubmitted" means exactly what the draft
 * warning and the submit gate already mean by it — an `isChanged` row whose
 * values differ from its committed signature, not merely a flagged row.
 */
export function hasUnsyncedLocalWorkOnEmployee(
  employee: EmployeeRow | null | undefined
): boolean {
  return (employee?.labors ?? []).some((labor) =>
    hasForemanLaborDraftEdits(labor)
  );
}

/**
 * True when the job holds anything the device has not synced, in which case it
 * must survive eviction even though the authoritative list no longer carries it.
 *
 * A job with no server identity at all is treated as local work by definition:
 * it was created on the device and is absent from every remote list until its
 * first sync, so evicting it would delete it the moment it was made.
 */
export function hasUnsyncedLocalWork(
  job: ForemanJobDetail | null | undefined
): boolean {
  if (!job) {
    return false;
  }
  if (!trimId(job.foremanJobID) && !trimId(job.jobID)) {
    return true;
  }
  if ((job.employees ?? []).some(hasUnsyncedLocalWorkOnEmployee)) {
    return true;
  }
  // Equipment usage carries its own isChanged flag and is submitted on the same
  // payload, so an equipment-only draft is just as unsent as a labor draft.
  return (job.equipment ?? []).some((dispatch) =>
    (
      (dispatch as { usages?: Array<{ isChanged?: boolean }> } | null)
        ?.usages ?? []
    ).some((usage) => usage?.isChanged === true)
  );
}

export interface EvictStaleCachedJobsOptions {
  /**
   * Whether an EMPTY remote list may evict. Defaults to FALSE: wiping the store
   * is the one irreversible thing this pass does, so a caller has to say out
   * loud that its empty list is authoritative rather than a failed fetch.
   *
   * The production caller passes true, having already separated a transport
   * failure from a real online empty (its `catchError` yields `null`, not `[]`).
   * Defaulting this to true would mean any future caller that has NOT made that
   * distinction wipes the device on a transient empty 200.
   */
  evictWhenRemoteEmpty?: boolean;
}

/** Index a remote job under both id fields so a sparse cache row still matches. */
function registerAliases(
  byAlias: Map<string, ForemanJobDetail>,
  job: ForemanJobDetail
): void {
  const foremanJobId = trimId(job.foremanJobID);
  const jobId = trimId(job.jobID);
  if (foremanJobId) {
    byAlias.set(`f:${foremanJobId}`, job);
  }
  if (jobId) {
    byAlias.set(`j:${jobId}`, job);
  }
}

function findRemoteMatch(
  remoteByAlias: Map<string, ForemanJobDetail>,
  job: ForemanJobDetail
): ForemanJobDetail | undefined {
  const foremanJobId = trimId(job.foremanJobID);
  const jobId = trimId(job.jobID);
  // Match on EITHER id: cache rows written by older builds can be jobID-only
  // while remote is foremanJobID-first (#30176), and a single-field match would
  // evict a job that is in fact still on the list.
  return (
    (foremanJobId ? remoteByAlias.get(`f:${foremanJobId}`) : undefined) ??
    (jobId ? remoteByAlias.get(`j:${jobId}`) : undefined)
  );
}

/**
 * Drop cached employee rows the authoritative copy of the same job no longer
 * carries. Rows with no `foremanEmployeeJobID` have no server identity and are
 * kept; rows holding unsubmitted labor are kept.
 */
function pruneEmployeesAgainstRemote(
  job: ForemanJobDetail,
  remoteJob: ForemanJobDetail
): ForemanJobDetail {
  // `undefined` means the response did not speak to the roster at all, which is
  // not the same as an authoritative empty roster.
  if (!Array.isArray(remoteJob.employees)) {
    return job;
  }
  const cached = job.employees ?? [];
  if (!cached.length) {
    return job;
  }

  const remoteKeys = new Set(
    remoteJob.employees
      .map((employee) => trimId(employee?.foremanEmployeeJobID))
      .filter(Boolean)
  );

  const employees = cached.filter((employee) => {
    const key = trimId(employee?.foremanEmployeeJobID);
    if (!key) {
      return true;
    }
    return remoteKeys.has(key) || hasUnsyncedLocalWorkOnEmployee(employee);
  });

  if (employees.length === cached.length) {
    return job;
  }
  return { ...job, employees };
}

/**
 * Reconcile the local job store against an authoritative remote list: remove
 * jobs the server no longer returns, and inside the jobs it does return, remove
 * orphaned employee rows. Unsynced local work is never dropped.
 */
export function evictStaleCachedJobs(
  cachedJobs: ForemanJobDetail[] | null | undefined,
  remoteJobs: ForemanJobDetail[] | null | undefined,
  options?: EvictStaleCachedJobsOptions
): ForemanJobDetail[] {
  const cached = cachedJobs ?? [];
  const remote = remoteJobs ?? [];
  const evictWhenRemoteEmpty = options?.evictWhenRemoteEmpty ?? false;

  if (!remote.length && !evictWhenRemoteEmpty) {
    return cached;
  }

  const remoteByAlias = new Map<string, ForemanJobDetail>();
  for (const remoteJob of remote) {
    registerAliases(remoteByAlias, remoteJob);
  }

  const kept: ForemanJobDetail[] = [];
  for (const job of cached) {
    const remoteJob = findRemoteMatch(remoteByAlias, job);
    if (!remoteJob) {
      if (hasUnsyncedLocalWork(job)) {
        kept.push(job);
      }
      continue;
    }
    kept.push(pruneEmployeesAgainstRemote(job, remoteJob));
  }
  return kept;
}
